import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { api, getSession } from './api';

const STORAGE_KEY = 'bfi_theme';

const ThemeContext = createContext({
  theme: 'light',
  isDark: false,
  toggleTheme: () => {},
  setTheme: () => {},
});

function applyHtmlClass(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
}

export function ThemeProvider({ children }) {
  // Initial value : localStorage > system preference > 'light'
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    return 'light';
  });

  // Apply class on theme change
  useEffect(() => {
    applyHtmlClass(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch {}
  }, [theme]);

  // Hydrate from user profile (dark_mode) once logged in
  useEffect(() => {
    const { token, user } = getSession();
    if (!token || !user) return;
    // Only call /client/profile for clients (admins might 403)
    if (user.role !== 'client') return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/client/profile');
        if (cancelled) return;
        if (typeof data?.dark_mode === 'boolean') {
          const next = data.dark_mode ? 'dark' : 'light';
          setThemeState(next);
        }
      } catch {
        // Silent fallback : on garde la valeur locale
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setTheme = useCallback((next) => {
    setThemeState(next === 'dark' ? 'dark' : 'light');
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((cur) => {
      const next = cur === 'dark' ? 'light' : 'dark';
      // Persist to backend if logged in client - non blocking
      const { token, user } = getSession();
      if (token && user?.role === 'client') {
        api.patch('/client/profile', { dark_mode: next === 'dark' }).catch(() => {});
      }
      return next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, isDark: theme === 'dark', toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

/** Petit composant toggle réutilisable (sun/moon) */
export function ThemeToggle({ className = '', variant = 'icon' }) {
  const { isDark, toggleTheme } = useTheme();
  if (variant === 'pill') {
    return (
      <button
        onClick={toggleTheme}
        aria-label={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition ${isDark ? 'bg-slate-800 border-slate-700 text-amber-300 hover:bg-slate-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'} ${className}`}
        data-testid="theme-toggle"
      >
        {isDark ? <Sun className="w-3.5 h-3.5"/> : <Moon className="w-3.5 h-3.5"/>}
        {isDark ? 'Clair' : 'Sombre'}
      </button>
    );
  }
  if (variant === 'sidebar') {
    return (
      <button
        onClick={toggleTheme}
        aria-label={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-white/10 text-white/80 w-full text-left ${className}`}
        data-testid="theme-toggle"
      >
        {isDark ? <Sun className="w-4 h-4"/> : <Moon className="w-4 h-4"/>}
        {isDark ? 'Mode clair' : 'Mode sombre'}
      </button>
    );
  }
  return (
    <button
      onClick={toggleTheme}
      aria-label={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
      className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition ${isDark ? 'bg-slate-800 border-slate-700 text-amber-300 hover:bg-slate-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'} ${className}`}
      data-testid="theme-toggle"
    >
      {isDark ? <Sun className="w-4 h-4"/> : <Moon className="w-4 h-4"/>}
    </button>
  );
}
